import { OutlineElement } from '../../base/outline-element/outline-element';
import { CSSResultGroup, html, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import componentStyles from './tufts-menu.css.lit';
import '../tufts-icon/tufts-icon';
import '../tufts-heading/tufts-heading';

import {
  denormalize,
  MenuElementInterface,
  NormalizedMenuInterface,
} from 'decoupled-menu-parser-es6';

/**
 * The Tufts Menu Component
 *
 * @element tufts-menu
 *
 * @attr debug-menu
 * @attr menu-id
 * @attr menu-name
 * @attr menu-type
 * @attr menu-heading-size
 * @attr mobile
 * @attr color-mode
 * @attr justify
 * @attr mobile-trigger
 */
@customElement('tufts-menu')
export class TuftsMenu extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  tree: MenuElementInterface[];
  mobileStyle = 'none';
  isLoading = false;
  loadingMessage = 'Loading...';

  /**
   * Wether to use real from URL or debug menu.
   */
  @property({
    type: Boolean,
    reflect: true,
    attribute: 'debug-menu',
  })
  debugMenu: boolean;

  /**
   * Special case to strip link from top level items
   */
  @property({
    type: Boolean,
    reflect: true,
    attribute: 'top-menu-no-link',
  })
  topMenuNoLink = false;

  /**
   * Wether the menu item should be a button or text
   */
  @property({
    type: Boolean,
    reflect: true,
    attribute: 'menu-item-as-header',
  })
  menuItemAsHeader = false;

  /**
   * Menu machine name.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'menu-id',
  })
  menuId = 'main-menu';

  /**
   * Menu full name (for screen-readers).
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'menu-name',
  })
  menuName: string;

  /**
   * Menu language.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'menu-lang',
  })
  menuLang = '';

  /**
   * Menu Type - 'menu' or 'groupcontentmenu'.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'menu-type',
  })
  menuType = 'menu';

  /**
   * Use text items as dropdown buttons or as titles above children.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'menu-heading-size',
  })
  menuHeadingSize = 'xs';

  /**
   * Force mobile menu (vertical) display.
   */
  @property({
    type: Boolean,
    reflect: true,
    attribute: 'mobile',
  })
  isMobile = false;

  /**
   * Color mode (light or dark).
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'color-mode',
  })
  colorMode = 'light';

  /**
   * Should menu be justified to the right.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'justify',
  })
  justify: string;

  /**
   * Whether menu can trigger mobile/desktop switch.
   */
  @property({
    type: Boolean,
    reflect: true,
    attribute: 'mobile-trigger',
  })
  mobileTrigger = false;

  /**
   * Slice of menu to display (for multi-level menus).
   */
  @property({
    type: Number,
    reflect: true,
    attribute: 'initial-menu-depth',
  })
  initialMenuDepth = undefined;

  /**
   * Whether to keep first item or not. Can only be used if base-item attribute is set.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'keep-base-item',
  })
  keepBaseItem = null;

  /**
   * How many levels of menu to display.
   */
  @property({
    type: Number,
    reflect: true,
    attribute: 'depth',
  })
  maxMenuDepth: number;

  /**
   * Whether menu dropdown should open/close automatically on hover
   */
  @property({
    type: Boolean,
    reflect: true,
    attribute: 'manual-mode',
  })
  manualMode = undefined;

  /**
   * Path of current page
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'active-page',
  })
  activePage: string;

  /**
   * JSON data
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'json',
  })
  json: string;

  currentMenu: NormalizedMenuInterface;
  jsonMenuURL: string;

  connectedCallback() {
    super.connectedCallback();

    if (this.json) {
      this.currentMenu = JSON.parse(this.json) as NormalizedMenuInterface;
      this.processJsonIntoMenu();
    } else if (this.menuId) {
      this.fetchData();
    }
  }

  closeMenuOnBlur() {
    const dropDownMenus = this.shadowRoot?.querySelectorAll('button ~ ul');
    dropDownMenus?.forEach(menu => {
      const allLinks = menu.querySelectorAll('a');
      const lastLink = allLinks[allLinks.length - 1];
      lastLink.addEventListener('blur', this.closeBlurMenu);
    });
  }

  closeBlurMenu(event: FocusEvent) {
    let buttonFound = false;
    let currentElement = event.target as HTMLElement;

    while (
      !buttonFound &&
      !currentElement.classList.contains('menu--level-2')
    ) {
      const closestUL = currentElement.parentElement?.closest('ul');
      const uncleElement = closestUL?.previousElementSibling;

      // Some submenus don't have dropdown button, the list is always expanded
      // If button found - close it, if not - go up one level
      if (uncleElement instanceof HTMLButtonElement) {
        uncleElement.setAttribute('aria-expanded', 'false');
        buttonFound = true;
      } else if (closestUL) {
        currentElement = closestUL;
      }
    }
  }

  closeMenu(event: FocusEvent) {
    const currentElement = event.target as HTMLElement;
    const button = currentElement.querySelector('button');
    button?.setAttribute('aria-expanded', 'false');
  }

  static openMenu(e: Event) {
    const target = e.target as HTMLElement;
    // If touch event, prevent mouseover event from triggering the submenu
    if (target.classList.contains('is-touch-event')) {
      target.classList.remove('is-touch-event');
      return;
    }

    // Do not close other menus unless the click is on the top level menu
    if (target.classList.contains('menu--level-1')) {
      TuftsMenu.closeAllMenusEvent(e);
    }

    const toggleButton = target.querySelector('button');
    toggleButton?.setAttribute('aria-expanded', 'true');
  }

  private _handleTouchStart(e: Event) {
    const target = e.target as HTMLElement;
    // If touch event, prevent mouseover event from triggering the submenu (gets removed in openMenu function)
    target.closest('li')?.classList.add('is-touch-event');
  }

  mobileTriggerMenu: HTMLElement | null | undefined;

  /**
   * Set up Resize Observer to listen for changes to the size of the primary
   * navigation.
   *
   * @param {Element} primaryNav - The primary navigation's top-level <ul> element.
   */
  menuReady() {
    this.requestUpdate();
    // After timeout of 0ms, the menu is in the DOM
    setTimeout(() => {
      this.closeMenuOnBlur();
    }, 0);
  }

  /**
   * This function performs a search on an array of objects to find the first item that matches the "hierarchy" property of a given active-page from Drupal. The search is based on the first three elements of the "hierarchy" property of the active-page, sliced from the full "hierarchy" array. The initial-menu-depth parameter is used to limit the search to the specified depth of the "hierarchy" array.
   * @param {string} activePage - The path of the currently active page.
   * @param {number} initialMenuDepth - The depth of the menu to display.
   * @param {NormalizedMenuInterface} jsonMenu - The menu data.
   * @returns {NormalizedMenuInterface} - The menu data.
   * @example
   * sliceMenu('/get-care/locations/circle-health/sub-test-2', 3, jsonMenu);
   * // => { "title": "Circle Health", "url": "/get-care/locations/circle-health", "hierarchy": ["0", "1", "0"] }
   *
   * Steps:
   * 1. Receives active-page from Drupal in the format "/get-care/locations/circle-health/sub-test-2".
   * 2. Look into that item's "hierarchy": ["0", "1", "0", "3", "2", "1"].
   * 3. According to initial-menu-depth (3), search json for the first item with that hierarchy, sliced with 3 first items ("hierarchy": ["0", "1", "0"]).
   * This is the base item.
   */

  sliceMenu() {
    if (
      !this.initialMenuDepth ||
      !this.activePage ||
      !this.currentMenu.linkset[0]
    ) {
      return;
    }
    const jsonMenuItems = this.currentMenu.linkset[0].item as unknown as {
      'href': string;
      'title': string;
      'hierarchy': string[];
      'machine-name': string[];
    }[];

    // find index of active page in jsonMenu
    const activePageIndex = jsonMenuItems.findIndex(item => {
      return JSON.stringify(item.href) === JSON.stringify(this.activePage);
    });

    // If active page is not found in menu, don't display the menu.
    if (activePageIndex === -1) {
      this.currentMenu.linkset[0].item = [];
      return;
    }

    // find top item in jsonMenu, according to initialMenuDepth
    const baseItemIndex = jsonMenuItems.findIndex(item => {
      return (
        JSON.stringify(item.hierarchy) ===
        JSON.stringify(
          jsonMenuItems[activePageIndex].hierarchy.slice(
            0,
            this.initialMenuDepth
          )
        )
      );
    });

    const baseItem = jsonMenuItems[baseItemIndex];

    const baseItemDepth = jsonMenuItems[baseItemIndex].hierarchy.length;

    const filteredJsonMenuItems = jsonMenuItems.filter((item, index) => {
      // remove base item
      if (index === baseItemIndex) {
        if (!this.keepBaseItem) {
          return false;
        }
      }

      // Make sure menu item has same base hierarchy
      return baseItem.hierarchy.every(
        (val, index) => val === item.hierarchy[index]
      );
    });

    const slicedJsonMenuItems = filteredJsonMenuItems.map(item => {
      item.hierarchy = item.hierarchy.slice(baseItemDepth);
      return item;
    });

    this.currentMenu.linkset[0].item = slicedJsonMenuItems;
  }

  limitMenuDepth() {
    if (!this.maxMenuDepth || !this.currentMenu.linkset[0]) {
      return;
    }

    const jsonMenuItems = this.currentMenu.linkset[0].item;

    // If base item is kept - modify depth calculation
    const depth =
      this.maxMenuDepth - (this.initialMenuDepth && this.keepBaseItem ? 1 : 0);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.currentMenu.linkset[0].item = jsonMenuItems.filter((item: any) => {
      return item.hierarchy.length <= depth!;
    });
  }

  static menuLevelTemplate(levels: TemplateResult[], menuDepth: number) {
    return html`<ul class="menu--level-${menuDepth}">
      ${levels}
    </ul>`;
  }

  buttonTemplate(
    classes: string,
    showTitle: boolean,
    title: string | undefined
  ) {
    return html`<button
      class=${classes}
      type="button"
      @click="${TuftsMenu.toggleMenu}"
      aria-expanded="false"
      aria-haspopup="true"
    >
      ${showTitle
        ? html`<span>${title}</span>`
        : html`<span class="visually-hidden">${title} sub-navigation</span>`}
      <span class="dropdown-icon">
        <tufts-icon
          name="chevron-down"
          library="system"
          size="1rem"
        ></tufts-icon>
      </span>
    </button>`;
  }

  menuLinkAndParentTemplate(
    title: string | undefined,
    href: string,
    children: MenuElementInterface['children'],
    menuDepth: number
  ) {
    const manualMode = this.manualMode || this.isMobile;
    const menuLevelClass = `no-title dropdown-button menu--level-${menuDepth} menu-link--level-${menuDepth}`;
    return html`<li
      class="menu--level-${menuDepth} menu-item--level-${menuDepth}"
      @mouseenter="${ifDefined(manualMode ? undefined : TuftsMenu.openMenu)}"
      @mouseleave="${ifDefined(manualMode ? undefined : this.closeMenu)}"
      @touchstart="${this._handleTouchStart}"
    >
      ${href !== '#'
        ? html`<a
            href="${href}"
            class="menu--level-${menuDepth} menu-link--level-${menuDepth} menu-item"
            >${title}</a
          >`
        : html`<span
            class="menu--level-${menuDepth} menu-link--level-${menuDepth} menu-item"
            >${title}</span
          >`}
      ${this.buttonTemplate(menuLevelClass, false, title)}
      ${this.renderMenuLevel(children)}
    </li>`;
  }

  menuParentTemplate(
    title: string | undefined,
    children: MenuElementInterface['children'],
    menuDepth: number
  ) {
    const manualMode = this.manualMode || this.isMobile;
    return html`<li
      class="menu--level-${menuDepth} menu-item--level-${menuDepth}"
      @mouseenter="${ifDefined(manualMode ? undefined : TuftsMenu.openMenu)}"
      @mouseleave="${ifDefined(manualMode ? undefined : this.closeMenu)}"
      @touchstart="${this._handleTouchStart}"
    >
      ${this.menuItemAsHeader
        ? html`
            <tufts-heading level-size="${this.menuHeadingSize}"
              ><h3>${title}</h3></tufts-heading
            >
          `
        : this.buttonTemplate('with-title dropdown-button', true, title)}
      ${this.renderMenuLevel(children)}
    </li>`;
  }

  static menuLinkTemplate(
    title: string | undefined,
    href: string,
    menuDepth: number
  ) {
    return html`<li
      class="menu--level-${menuDepth} menu-link--level-${menuDepth}"
    >
      ${href !== '#'
        ? html`<a href="${href}" class="menu-item">${title}</a>`
        : html`<span class="menu-item">${title}</span>`}
    </li>`;
  }

  static menuItemTemplate(title: string | undefined, menuDepth: number) {
    return html`<li
      class="menu--level-${menuDepth} menu-item--level-${menuDepth}"
    >
      <span class="menu-item"> ${title} </span>
    </li>`;
  }

  renderMenuLevel(level: MenuElementInterface['children']): TemplateResult {
    const levels = level.map((item: MenuElementInterface) => {
      let menuDepth = 0;
      if (Array.isArray(item.link.attributes.hierarchy)) {
        menuDepth = item.link.attributes.hierarchy.length;
      }
      return this.renderMenuItem(item, menuDepth);
    });

    return TuftsMenu.menuLevelTemplate(levels, levels[0].values[0] as number);
  }

  renderMenuItem(item: MenuElementInterface, menuDepth: number) {
    const title = item.link.attributes.title;
    const href = item.link.href;
    const children = item.children;
    if (!this.topMenuNoLink) {
      if (href && children && children.length) {
        return this.menuLinkAndParentTemplate(title, href, children, menuDepth);
      }
    }
    if (children && children.length) {
      return this.menuParentTemplate(title, children, menuDepth);
    }
    if (href) {
      return TuftsMenu.menuLinkTemplate(title, href, menuDepth);
    }
    return TuftsMenu.menuItemTemplate(title, menuDepth);
  }

  getDebugMenu() {
    return './dist/assets/menu-debug/' + this.menuId + '-menu.json';
  }

  getDrupalMenu() {
    // /system/menu/main/linkset
    // /es/system/menu/main/linkset
    // /system/groupcontentmenu/1/linkset
    // /es/system/groupcontentmenu/1/linkset

    const baseUrl = location.protocol + '//' + location.host;
    const langURL = this.menuLang ? '/' + this.menuLang : '';
    const drupalMenuURL =
      baseUrl + langURL + `/system/${this.menuType}/${this.menuId}/linkset`;
    return drupalMenuURL;
  }

  async fetchData() {
    this.isLoading = true;

    this.jsonMenuURL = this.debugMenu
      ? this.getDebugMenu()
      : this.getDrupalMenu();

    const response = await fetch(this.jsonMenuURL);
    if (!response.ok) {
      const message = `Unable to fetch ${this.jsonMenuURL}. ${response.status} ${response.statusText}`;
      throw new Error(message);
    }

    this.currentMenu = await response.json();

    this.processJsonIntoMenu();
  }

  processJsonIntoMenu() {
    this.sliceMenu();
    this.limitMenuDepth();

    const denormalized = denormalize(this.currentMenu, this.menuId);
    if (!Array.isArray(denormalized)) {
      this.tree = denormalized.tree;
      this.isLoading = false;
      this.menuReady();
    }
  }

  static closeAllMenusEvent(e: Event) {
    TuftsMenu.closeAllMenus(e.target as HTMLButtonElement);
  }

  static closeAllMenus(toggleButton: HTMLButtonElement) {
    const header = document.querySelector('tufts-header');

    const menus = header?.shadowRoot?.querySelectorAll('tufts-menu');

    if (menus) {
      [...menus].forEach(menu => {
        const buttons = menu.shadowRoot?.querySelectorAll('button');
        if (buttons) {
          [...buttons].forEach(button => {
            if (button !== toggleButton) {
              button.setAttribute('aria-expanded', 'false');
            }
          });
        }
      });
    }
  }

  static toggleMenu(e: Event) {
    const target = e.target as HTMLElement;
    const toggleButton = target.closest('button');
    if (toggleButton) {
      const isExpanded = toggleButton.getAttribute('aria-expanded') === 'true';

      if (isExpanded) {
        toggleButton.setAttribute('aria-expanded', 'false');
      } else {
        toggleButton.setAttribute('aria-expanded', 'true');
        TuftsMenu.closeAllMenus(toggleButton);
      }
    }
  }

  render() {
    if (this.isLoading) return null;
    // Don't display a menu if there is no data
    if (this.tree.length === 0) return null;

    return html`
      <nav
        class="${this.isMobile ? 'mobile' : ''} ${this.colorMode} ${this
          .justify} ${this.menuId}-menu"
        id="${this.menuId}-menu"
        aria-labelledby="${this.menuId}-menu-title"
      >
        <h2 class="visually-hidden" id="${this.menuId}-menu-title">
          ${this.menuName}
        </h2>
        ${this.renderMenuLevel(this.tree)}
      </nav>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tufts-menu': TuftsMenu;
  }
}
